/**
 * Générateur — Introduire une lettre pour désigner une inconnue et mettre un
 * problème en équation.
 * Attendu ID : 161 | Niveau : 4ème | Domaine : Calcul littéral (O25)
 *
 * Source : doc prof « TANOED EXERCISEUR … 4EME », NOMBRE ET CALCUL, cluster 3,
 * O25. Critère de réussite (image page30) : mettre en équation « triangle
 * équilatéral + carré, même périmètre ? ». Exercices : Ex.7 (Medhi ×4 −7 vs
 * Sarah +3 ×2 → 4x − 7 = 2(x + 3)), Ex.8 (250 billes, 18 rouges de plus).
 *
 * Objectif = MODÉLISER (écrire l'équation), pas la résoudre (→ O27/gen_163).
 *
 * Plan de cartes (validé 2026-07-14, historique) :
 *   equation   — problème → quelle équation le traduit (qcm, 4 contextes).
 *   traduire   — « le double de x augmenté de 3 » ↔ expression (relier + qcm).
 *   perimetre  — périmètres égaux triangle/carré : quelle équation (qcm + SVG) +
 *                exprimer le périmètre d'un polygone régulier (saisie).
 *
 * Revue 2026-09-23 (vague 153-163), décisions du coordinateur appliquées en
 * mode auto. Attendu de fin de 4e (Eduscol, attendus de fin d'année, p. 4) :
 * « Il introduit une lettre pour désigner une valeur inconnue et met un
 * problème en équation. » Exemple de réussite : « On juxtapose un triangle
 * équilatéral et un carré comme schématisé ci-contre. Est-il possible que le
 * triangle et le carré aient le même périmètre ? » ; la figure montre les deux
 * figures ACCOLÉES et une seule cote, 14 cm, sous l'ensemble, aucune lettre ;
 * mise en équation attendue 3x = 4(14 − x).
 *   - KaTeX partout (règle prof) : la lettre x des énoncés, les quatre choix de
 *     chaque QCM, les cibles ET les leurres du relier, par tex() ; moins ASCII,
 *     fractions en \dfrac, puissances en ^.
 *   - Carte « equation » GARDÉE, corrigée. A1 : le contexte « prix » tire le
 *     prix du classeur différent de celui du cahier (p = q rendait un
 *     distracteur équivalent à la clé, 3,4 % des tirages). A7 : « machines »
 *     tire les deux multiplicateurs différents (égaux, aucun nombre ne donnait
 *     le même résultat : problème impossible, 5,1 %). Billes : « Un sac ne
 *     contient que des billes rouges et des billes noires » (la clé x + (x + d)
 *     le suppose). « Medhi et Sarah obtiennent le même résultat » (plus de
 *     « Ils »). Prix : « On note x le nombre de cahiers » (la lettre sortie du
 *     récit, comme les trois autres contextes). Le contexte âge x + n = A, geste
 *     de 5e, est REMPLACÉ par l'abonnement de cinéma du cours (carte à C €,
 *     chaque entrée à e €, T € en tout : ex + C = T). Machines, prix et
 *     abonnement : grille de DEUX erreurs croisées (la clé, chaque erreur
 *     seule, les deux ensemble), les quatre options symétriques, fin de la
 *     clé « la plus proche des autres » (75 % des tirages). Billes, sans
 *     grille naturelle, garde ses trois distracteurs ; signe s'y écrit
 *     x + (x − d) = T, la même équation que 2x − d = T sous la forme de la
 *     clé (sinon la clé était la seule option longue, 25 % des tirages).
 *     Toute option est relue en polynôme exact : quatre solutions deux à deux
 *     distinctes exigées, nouveau tirage sinon.
 *   - Cartes « traduire » (relier, QCM) RECADRÉES de « phrase vers
 *     expression » en « phrase vers ÉQUATION » (mode auto, À ARBITRER :
 *     traduire une phrase en EXPRESSION est un geste de 5e et de
 *     l'automatisme 1110 ; l'attendu dit « met un problème en équation »).
 *     Libellé « Du langage à l'équation ». Les mauvaises traductions gardent
 *     LES nombres de la phrase (la clé était la seule option qui portait le
 *     nombre de la phrase dans 50,7 % des tirages). Relier : trois phrases,
 *     cinq équations dont deux leurres nommés, nouveau tirage tant que deux
 *     leurres ne survivent pas (7,1 % des tirages n'en avaient aucun alors que
 *     l'énoncé l'annonçait), « Deux équations sont en trop. », « on note x le
 *     nombre » en phrase. QCM : trois mauvaises traductions nommées de LA
 *     phrase, sur les quatre familles qui en portent trois (double,
 *     triple augmenté, produit, double diminué) ; « double diminué » les a en
 *     grille parenthesage × ordre_soustraction (les_deux 2(b − x) = c), avec
 *     x² − b = c à la place la clé était l'option la plus proche des trois
 *     autres dans toute la famille. Choix mode auto, À ARBITRER : « Le triple
 *     d'un nombre, augmenté de 7, vaut 25. » s'écrit avec les virgules, sans
 *     elles la phrase admet aussi la lecture 3(x + 7), qui est justement le
 *     piège parenthesage.
 *   - Carte « perimetre » : le QCM est RECADRÉ sur l'exemple de réussite
 *     (triangle et carré accolés, longueur totale L de 10 à 20 cm cotée sous
 *     l'ensemble, aucune lettre sur la figure ; x = côté du triangle ; clé
 *     3x = 4(L − x)). Distracteurs parentheses_ignorees 3x = 4 × L − x,
 *     roles_echanges 4x = 3(L − x), donnee_oubliee 3x = 4 × L (mode auto, À
 *     ARBITRER : la décision laissait somme_pour_difference 3x = 4(L + x) ou
 *     donnee_oubliee ; avec somme_pour_difference la clé redevient l'option la
 *     plus proche des trois autres, avec donnee_oubliee non). La figure est
 *     dessinée aux proportions de l'exemple (côtés 4 et 3, soit 8 cm et 6 cm
 *     pour 14 cm), triangle à gauche ou à droite (mode auto, À ARBITRER),
 *     tout dans le viewBox (le haut du carré était coupé dans 100 % des
 *     tirages), attribut alt. La saisie « périmètre d'un polygone régulier »
 *     (perimetre_texte_trou_saisie) est SORTIE : exprimer un périmètre réduit,
 *     c'est développer n(x + k), l'attendu 159 ; « régulier » n'est ni dans
 *     les attendus de 4e ni dans le cours.
 *   - Rappel brouillon : AUCUN (mode auto, À ARBITRER) : mettre en équation se
 *     fait de tête, rien n'est à calculer.
 *   - Contrat des distracteurs exporté (REGLES_DISTRACTEURS_QCM), codes dans
 *     _meta.params.distracteurs.
 * Quatre formats, dont trois QCM.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeRelier, tex,
} from '../../../utils';
import { rat } from './litteral';

const ATTENDU_ID = 161;

const L_EQUATION = 'Mettre un problème en équation';
const L_TRADUIRE = 'Du langage à l\'équation';
const L_PERIMETRE = 'Périmètres et mise en équation';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ Équations exactes ═════════
// Une équation affichée porte son polynôme P = membre de gauche − membre de
// droite (rationnels, indice = degré). C'est le second chemin : la clé doit
// s'annuler en la solution calculée depuis l'HISTOIRE (throw sinon), et aucun
// distracteur ne doit s'y annuler.

const R = (p, q = 1) => rat(p, q);
const rAdd = (a, b) => rat(a.p * b.q + b.p * a.q, a.q * b.q);
const rMul = (a, b) => rat(a.p * b.p, a.q * b.q);
const rEgal = (a, b) => a.p === b.p && a.q === b.q;
const fmtR = (r) => (r.q === 1 ? String(r.p) : `${r.p}/${r.q}`);

/** Coefficients entiers ou rationnels → polynôme de rationnels. */
const poly = (...coefs) => coefs.map((c) => (typeof c === 'number' ? R(c) : c));

/** Valeur de P en s (rationnel). */
const evalP = (P, s) => {
    let acc = R(0);
    let puissance = R(1);
    P.forEach((c) => {
        acc = rAdd(acc, rMul(c, puissance));
        puissance = rMul(puissance, s);
    });
    return acc;
};

/** Solution unique d'une équation du premier degré, null sinon. */
const solution1 = (P) => {
    const degre = P.reduce((d, c, i) => (c.p !== 0 ? i : d), -1);
    if (degre !== 1) return null;
    return rat(-P[0].p * P[1].q, P[0].q * P[1].p);
};

/** Une option : l'écriture LaTeX (sans $), son polynôme, son code d'erreur (null = clé). */
const E = (latex, P, code = null) => ({ latex, P, code });

/**
 * Contrôle d'un QCM d'équations du premier degré : la clé s'annule en s et n'a
 * qu'une solution (sinon throw : erreur de construction) ; chaque option a UNE
 * solution, les quatre deux à deux distinctes, écritures distinctes (sinon
 * false : nouveau tirage).
 */
const optionsSaines = (options, s, contexte) => {
    const [cle] = options;
    const sc = solution1(cle.P);
    if (!sc || !rEgal(sc, s) || evalP(cle.P, s).p !== 0) {
        throw new Error(`gen_161 : clé ${cle.latex} non vérifiée par ${fmtR(s)} (${contexte})`);
    }
    const sols = options.map((o) => solution1(o.P));
    if (sols.some((x) => !x)) return false;
    const cles = new Set(sols.map(fmtR));
    if (cles.size !== options.length) return false;
    return new Set(options.map((o) => o.latex)).size === options.length;
};

// ═════════ Carte 1 — Mettre un problème en équation ═════════

const QUESTION_EQUATION = '<p><b>Quelle équation traduit ce problème ?</b></p>';

// Chaque contexte rend { texte, s, options } ; s = la solution calculée depuis
// l'histoire, par un chemin qui n'utilise pas l'équation.
const CONTEXTES_EQUATION = {
    billes: () => {
        const d = randInt(6, 30);
        const k = randInt(2, 9);
        const T = 20 * k + d;                       // 10k noires, 10k + d rouges
        const noires = 10 * k;
        if (noires + (noires + d) !== T) throw new Error('gen_161 : billes');
        return {
            texte: `<p>Un sac ne contient que des billes rouges et des billes noires, ${T} billes en tout. `
                + `Il y a ${d} billes rouges de plus que de billes noires. On note ${tex('x')} le nombre de billes noires.</p>`,
            s: R(noires),
            // signe écrit x + (x − d), la même équation que 2x − d mais sous la
            // forme de la clé : sinon la clé est la seule option longue (25 %)
            options: [
                E(`x + (x + ${d}) = ${T}`, poly(d - T, 2)),
                E(`x + ${d} = ${T}`, poly(d - T, 1), 'une_seule_couleur'),
                E(`x + (x - ${d}) = ${T}`, poly(-d - T, 2), 'signe'),
                E(`x - ${d} = ${T}`, poly(-d - T, 1), 'une_couleur_et_signe'),
            ],
            params: { d, T },
        };
    },
    machines: () => {
        const a = randInt(2, 6);
        const b = randInt(2, 9);
        const c = randInt(2, 9);
        const dd = randomChoice([2, 3, 4, 5, 6].filter((v) => v !== a));   // égaux : problème impossible
        // Chemin 2 : Medhi(x) = ax − b, Sarah(x) = dd(x + c) ; égalité en rationnels.
        const s = R(dd * c + b, a - dd);
        if (a * s.p - b * s.q !== dd * (s.p + c * s.q)) throw new Error('gen_161 : machines');
        return {
            texte: `<p><b>Medhi</b> choisit un nombre, le multiplie par ${a}, puis soustrait ${b} au résultat. `
                + `<b>Sarah</b> choisit le même nombre, lui ajoute ${c}, puis multiplie le résultat par ${dd}. `
                + `Medhi et Sarah obtiennent le même résultat. On note ${tex('x')} le nombre choisi.</p>`,
            s,
            options: [
                E(`${a}x - ${b} = ${dd}(x + ${c})`, poly(-b - dd * c, a - dd)),
                E(`${a}x + ${b} = ${dd}(x + ${c})`, poly(b - dd * c, a - dd), 'signe'),
                E(`${a}x - ${b} = ${dd}x + ${c}`, poly(-b - c, a - dd), 'sans_parentheses'),
                E(`${a}x + ${b} = ${dd}x + ${c}`, poly(b - c, a - dd), 'les_deux'),
            ],
            params: { a, b, c, dd },
        };
    },
    prix: () => {
        const p = randInt(2, 6);
        const q = randomChoice([2, 3, 4, 5, 6, 7, 8, 9].filter((v) => v !== p));   // p = q : distracteur équivalent
        const n = randInt(4, 9);
        const T = p * n + q;
        if ((T - q) / p !== n) throw new Error('gen_161 : prix');
        return {
            texte: `<p>J'achète des cahiers à ${p} € l'un et un classeur à ${q} €. Je paie ${T} € en tout. `
                + `On note ${tex('x')} le nombre de cahiers.</p>`,
            s: R(n),
            options: [
                E(`${p}x + ${q} = ${T}`, poly(q - T, p)),
                E(`${p}x - ${q} = ${T}`, poly(-q - T, p), 'signe'),
                E(`${q}x + ${p} = ${T}`, poly(p - T, q), 'prix_echanges'),
                E(`${q}x - ${p} = ${T}`, poly(-p - T, q), 'les_deux'),
            ],
            params: { p, q, T },
        };
    },
    abonnement: () => {
        const C = randomChoice([10, 12, 15, 18, 20, 24, 25, 30]);
        const e = randInt(4, 9);
        const n = randInt(3, 12);
        const T = e * n + C;
        if ((T - C) / e !== n) throw new Error('gen_161 : abonnement');
        return {
            texte: `<p>Au cinéma, j'achète une carte d'abonnement à ${C} €, puis chaque entrée me coûte ${e} €. `
                + `J'ai payé ${T} € en tout, carte comprise. On note ${tex('x')} le nombre d'entrées.</p>`,
            s: R(n),
            options: [
                E(`${e}x + ${C} = ${T}`, poly(C - T, e)),
                E(`${e}x - ${C} = ${T}`, poly(-C - T, e), 'signe'),
                E(`${C}x + ${e} = ${T}`, poly(e - T, C), 'prix_echanges'),
                E(`${C}x - ${e} = ${T}`, poly(-e - T, C), 'les_deux'),
            ],
            params: { C, e, T },
        };
    },
};

const assemblerEquation = (ctx, t) => {
    const choix = t.options.map((o) => tex(o.latex));
    const payload = makeQcm(choix, choix[0], t.texte + QUESTION_EQUATION, opts(L_EQUATION));
    payload._meta.params = { ctx, ...t.params, solution: fmtR(t.s), distracteurs: t.options.slice(1).map((o) => o.code) };
    return payload;
};

const genEquationQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const ctx = randomChoice(Object.keys(CONTEXTES_EQUATION));
        const t = CONTEXTES_EQUATION[ctx]();
        if (!optionsSaines(t.options, t.s, ctx)) continue;
        return assemblerEquation(ctx, t);
    }
    // Repli déterministe vérifié : prix 3 € et 5 €, 7 cahiers, 26 € ;
    // solutions 7, 31/3, 23/5, 29/5, deux à deux distinctes.
    const t = {
        texte: `<p>J'achète des cahiers à 3 € l'un et un classeur à 5 €. Je paie 26 € en tout. On note ${tex('x')} le nombre de cahiers.</p>`,
        s: R(7),
        options: [E('3x + 5 = 26', poly(-21, 3)), E('3x - 5 = 26', poly(-31, 3), 'signe'),
            E('5x + 3 = 26', poly(-23, 5), 'prix_echanges'), E('5x - 3 = 26', poly(-29, 5), 'les_deux')],
        params: { p: 3, q: 5, T: 26 },
    };
    if (!optionsSaines(t.options, t.s, 'repli')) throw new Error('gen_161 : repli equation');
    return assemblerEquation('prix', t);
};

// ═════════ Carte 2 — Du langage à l'équation ═════════
//
// Une phrase dit une égalité sur « un nombre » ; la bonne traduction est une
// équation du premier degré, les mauvaises sont les erreurs de traduction
// nommées, TOUJOURS avec les nombres de la phrase (b, c). Les deux cartes
// tirent dans la même table ; le QCM ne prend que les familles qui portent
// trois erreurs nommées.

const PHRASES = {
    double: {
        phrase: (b, c) => `Le double d'un nombre vaut ${c}.`,
        cle: (b, c) => E(`2x = ${c}`, poly(-c, 2)),
        erreurs: (b, c) => [
            E(`x^2 = ${c}`, poly(-c, 0, 1), 'double_carre'),
            E(`x + 2 = ${c}`, poly(2 - c, 1), 'ajout_pour_double'),
            E(`\\dfrac{x}{2} = ${c}`, poly(-c, R(1, 2)), 'sens_inverse'),
        ],
    },
    triple_augmente: {
        phrase: (b, c) => `Le triple d'un nombre, augmenté de ${b}, vaut ${c}.`,
        cle: (b, c) => E(`3x + ${b} = ${c}`, poly(b - c, 3)),
        erreurs: (b, c) => [
            E(`3(x + ${b}) = ${c}`, poly(3 * b - c, 3), 'parenthesage'),
            E(`x + 3 + ${b} = ${c}`, poly(3 + b - c, 1), 'ajout_pour_double'),
            E(`x^3 + ${b} = ${c}`, poly(b - c, 0, 0, 1), 'triple_cube'),
        ],
    },
    produit: {
        phrase: (b, c) => `Le produit d'un nombre par ${b} vaut ${c}.`,
        cle: (b, c) => E(`${b}x = ${c}`, poly(-c, b)),
        erreurs: (b, c) => [
            E(`x + ${b} = ${c}`, poly(b - c, 1), 'produit_somme'),
            // x⁶ et au-delà ne sont plus une écriture qu'un élève de 4e propose
            ...(b <= 5 ? [E(`x^${b} = ${c}`, poly(-c, ...Array(b - 1).fill(0), 1), 'produit_puissance')] : []),
            E(`\\dfrac{x}{${b}} = ${c}`, poly(-c, R(1, b)), 'produit_quotient'),
        ],
    },
    // Grille parenthesage × ordre_soustraction (les_deux = 2(b − x)) : avec
    // x² − b à la place de les_deux, la clé était l'option la plus proche des
    // trois autres dans 100 % des tirages de la famille.
    double_diminue: {
        phrase: (b, c) => `Le double d'un nombre, diminué de ${b}, vaut ${c}.`,
        cle: (b, c) => E(`2x - ${b} = ${c}`, poly(-b - c, 2)),
        erreurs: (b, c) => [
            E(`2(x - ${b}) = ${c}`, poly(-2 * b - c, 2), 'parenthesage'),
            E(`${b} - 2x = ${c}`, poly(b - c, -2), 'ordre_soustraction'),
            E(`2(${b} - x) = ${c}`, poly(2 * b - c, -2), 'les_deux'),
        ],
    },
    somme: {
        phrase: (b, c) => `La somme d'un nombre et de ${b} vaut ${c}.`,
        cle: (b, c) => E(`x + ${b} = ${c}`, poly(b - c, 1)),
        erreurs: (b, c) => [
            E(`${b}x = ${c}`, poly(-c, b), 'somme_produit'),
            E(`x - ${b} = ${c}`, poly(-b - c, 1), 'difference_pour_somme'),
        ],
    },
    diminue: {
        phrase: (b, c) => `Un nombre diminué de ${b} vaut ${c}.`,
        cle: (b, c) => E(`x - ${b} = ${c}`, poly(-b - c, 1)),
        erreurs: (b, c) => [
            E(`${b} - x = ${c}`, poly(b - c, -1), 'ordre_soustraction'),
            E(`x + ${b} = ${c}`, poly(b - c, 1), 'somme_pour_difference'),
        ],
    },
    moitie_augmentee: {
        phrase: (b, c) => `La moitié d'un nombre, augmentée de ${b}, vaut ${c}.`,
        cle: (b, c) => E(`\\dfrac{x}{2} + ${b} = ${c}`, poly(b - c, R(1, 2))),
        erreurs: (b, c) => [
            E(`\\dfrac{x + ${b}}{2} = ${c}`, poly(R(b - 2 * c, 2), R(1, 2)), 'parenthesage'),
            E(`2x + ${b} = ${c}`, poly(b - c, 2), 'sens_inverse'),
        ],
    },
};

const FAMILLES_QCM = ['double', 'triple_augmente', 'produit', 'double_diminue'];

/** La clé d'une phrase, contrôlée : du premier degré et vérifiée par sa solution. */
const cleDe = (id, b, c) => {
    const cle = PHRASES[id].cle(b, c);
    const s = solution1(cle.P);
    if (!s || evalP(cle.P, s).p !== 0) throw new Error(`gen_161 : clé ${id} ${b} ${c}`);
    return { ...cle, s };
};

const CONSIGNE_RELIER = `<p><b>Relie chaque phrase à l'équation qui la traduit.</b> Dans chaque phrase, on note ${tex('x')} le nombre. Deux équations sont en trop.</p>`;

const assemblerRelier = (ids, b, c, cles, leurres) => {
    const pairs = ids.map((id, i) => ({ source: PHRASES[id].phrase(b, c), target: tex(cles[i].latex) }));
    const cibles = leurres.map((l) => ({ target: tex(l.latex), code: l.code }));
    const payload = makeRelier(shuffle(pairs), CONSIGNE_RELIER, { ...opts(L_TRADUIRE), cibles_leurres: cibles });
    payload._meta.params = {
        familles: ids, b, c,
        distracteurs: Object.fromEntries(cibles.map((l) => [l.target, l.code])),
    };
    return payload;
};

const genTraduireRelier = () => {
    for (let essai = 0; essai < 80; essai++) {
        const ids = shuffle(Object.keys(PHRASES)).slice(0, 3);
        const b = randInt(2, 9);
        const c = randInt(11, 40);
        const cles = ids.map((id) => cleDe(id, b, c));
        // trois phrases, trois solutions distinctes (deux clés de même solution
        // seraient deux équations interchangeables, « 2x = 25 » deux fois)
        if (new Set(cles.map((k) => fmtR(k.s))).size < 3) continue;
        const prises = new Set(cles.map((k) => k.latex));
        const candidats = shuffle(ids.flatMap((id) => PHRASES[id].erreurs(b, c).map((err) => ({ ...err, famille: id }))))
            // un leurre ne vaut ni une cible ni un autre leurre, et aucune
            // phrase ne le rend vrai : il ne traduit aucune des trois
            .filter((err) => !prises.has(err.latex) && cles.every((k) => evalP(err.P, k.s).p !== 0));
        const leurres = [];
        candidats.forEach((err) => {
            if (leurres.length >= 2 || leurres.some((l) => l.latex === err.latex)) return;
            // deux familles de phrases différentes quand c'est possible
            if (leurres.length === 1 && leurres[0].famille === err.famille
                && candidats.some((o) => o.famille !== err.famille && o.latex !== leurres[0].latex)) return;
            leurres.push(err);
        });
        if (leurres.length < 2) continue;
        return assemblerRelier(ids, b, c, cles, leurres);
    }
    // Repli déterministe vérifié : b = 7, c = 25 ; solutions 25/2, 6, 32 ;
    // leurres 3(x + 7) = 25 et 7 − x = 25 (aucun vrai pour 25/2, 6 ou 32).
    const ids = ['double', 'triple_augmente', 'diminue'];
    const cles = ids.map((id) => cleDe(id, 7, 25));
    const leurres = [PHRASES.triple_augmente.erreurs(7, 25)[0], PHRASES.diminue.erreurs(7, 25)[0]];
    if (leurres.some((l) => cles.some((k) => evalP(l.P, k.s).p === 0))) throw new Error('gen_161 : repli relier');
    return assemblerRelier(ids, 7, 25, cles, leurres);
};

/** Les quatre options d'une phrase, ou null si une mauvaise traduction est vraie pour sa solution. */
const optionsTraduire = (id, b, c) => {
    const cle = cleDe(id, b, c);
    const erreurs = PHRASES[id].erreurs(b, c);
    if (erreurs.length < 3) return null;
    // aucune mauvaise traduction n'est vraie pour la solution de la phrase
    if (erreurs.some((err) => evalP(err.P, cle.s).p === 0)) return null;
    const options = [cle, ...erreurs.slice(0, 3)];
    return new Set(options.map((o) => o.latex)).size === 4 ? options : null;
};

const assemblerTraduireQcm = (id, b, c, options) => {
    const choix = options.map((o) => tex(o.latex));
    const texte = `<p>« ${PHRASES[id].phrase(b, c)} »</p>`
        + `<p>On note ${tex('x')} ce nombre.</p>`
        + '<p><b>Quelle équation traduit cette phrase ?</b></p>';
    const payload = makeQcm(choix, choix[0], texte, opts(L_TRADUIRE));
    payload._meta.params = { famille: id, b, c, distracteurs: options.slice(1).map((o) => o.code) };
    return payload;
};

const genTraduireQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const id = randomChoice(FAMILLES_QCM);
        // produit : facteur de 3 à 5 (2 redonnerait « le double »)
        const b = id === 'produit' ? randInt(3, 5) : randInt(2, 9);
        const c = randInt(11, 40);
        const options = optionsTraduire(id, b, c);
        if (options) return assemblerTraduireQcm(id, b, c, options);
    }
    // Repli déterministe vérifié : la phrase de la décision, solution 6.
    const options = optionsTraduire('triple_augmente', 7, 25);
    if (!options) throw new Error('gen_161 : repli traduire');
    return assemblerTraduireQcm('triple_augmente', 7, 25, options);
};

// ═════════ Carte 3 — Périmètres et mise en équation ═════════
//
// La figure de l'exemple de réussite : triangle équilatéral et carré ACCOLÉS
// sur une même droite, une seule cote, la longueur totale, sous l'ensemble ;
// aucune lettre. Dessinée aux proportions de l'exemple (côtés 4 et 3 : 8 cm et
// 6 cm pour 14 cm), le triangle à gauche ou à droite.

const TRAIT = '#1E3A8A';

const svgJuxtaposition = (L, triangleAGauche) => {
    const u = 24;
    const cT = 4 * u;                               // côté du triangle (dessin)
    const cC = 3 * u;                               // côté du carré (dessin)
    const h = Math.round(cT * Math.sqrt(3) / 2 * 10) / 10;
    const x0 = 14;
    const base = Math.ceil(h) + 10;                 // sommet du triangle à y = 10,9 : dans le cadre
    const largeur = x0 + cT + cC + 14;
    const hauteur = base + 40;
    const xTri = triangleAGauche ? x0 : x0 + cC;
    const xCar = triangleAGauche ? x0 + cT : x0;
    const xFin = x0 + cT + cC;
    const yCote = base + 14;
    const tri = `<polygon points='${xTri},${base} ${xTri + cT},${base} ${xTri + cT / 2},${Math.round((base - h) * 10) / 10}' fill='#E0E7FF' stroke='${TRAIT}' stroke-width='2' stroke-linejoin='round'/>`;
    const car = `<rect x='${xCar}' y='${base - cC}' width='${cC}' height='${cC}' fill='#C7D2FE' stroke='${TRAIT}' stroke-width='2'/>`;
    // Cote : deux lignes de rappel, un trait à deux flèches, la longueur dessous.
    const rappels = `<line x1='${x0}' y1='${base + 4}' x2='${x0}' y2='${yCote + 6}' stroke='${TRAIT}' stroke-width='1'/>`
        + `<line x1='${xFin}' y1='${base + 4}' x2='${xFin}' y2='${yCote + 6}' stroke='${TRAIT}' stroke-width='1'/>`;
    const fleche = `<line x1='${x0 + 1}' y1='${yCote}' x2='${xFin - 1}' y2='${yCote}' stroke='${TRAIT}' stroke-width='1.5'/>`
        + `<path d='M ${x0} ${yCote} L ${x0 + 8} ${yCote - 4} L ${x0 + 8} ${yCote + 4} Z' fill='${TRAIT}'/>`
        + `<path d='M ${xFin} ${yCote} L ${xFin - 8} ${yCote - 4} L ${xFin - 8} ${yCote + 4} Z' fill='${TRAIT}'/>`;
    const cote = `<text x='${(x0 + xFin) / 2}' y='${yCote + 20}' text-anchor='middle' font-size='14' fill='${TRAIT}' font-family='sans-serif'>${L} cm</text>`;
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${largeur} ${hauteur}'>${tri}${car}${rappels}${fleche}${cote}</svg>`;
    return {
        version: 1,
        elements: [{
            type: 'image',
            config: {
                imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`,
                maxWidth: 'compact',
                alt: `Un triangle équilatéral et un carré accolés, posés sur une même droite, le ${triangleAGauche ? 'triangle' : 'carré'} à gauche ; `
                    + `la longueur totale, d'un bout à l'autre des deux figures, est de ${L} cm.`,
            },
        }],
    };
};

const genPerimetreQcm = () => {
    const L = randInt(10, 20);
    const triangleAGauche = randomChoice([true, false]);
    // Chemin 2 : le côté du carré est L − s ; périmètres 3s et 4(L − s), égaux.
    const s = R(4 * L, 7);
    const perimetreTriangle = rMul(R(3), s);
    const perimetreCarre = rMul(R(4), rAdd(R(L), rMul(R(-1), s)));
    if (!rEgal(perimetreTriangle, perimetreCarre)) throw new Error(`gen_161 : périmètres ${L}`);
    const options = [
        E(`3x = 4(${L} - x)`, poly(-4 * L, 7)),
        E(`3x = 4 \\times ${L} - x`, poly(-4 * L, 4), 'parentheses_ignorees'),
        E(`4x = 3(${L} - x)`, poly(-3 * L, 7), 'roles_echanges'),
        E(`3x = 4 \\times ${L}`, poly(-4 * L, 3), 'donnee_oubliee'),
    ];
    // solutions 4L/7, L, 3L/7, 4L/3 : deux à deux distinctes pour tout L > 0
    if (!optionsSaines(options, s, `perimetre ${L}`)) throw new Error(`gen_161 : perimetre ${L}`);
    const texte = '<p>On juxtapose un triangle équilatéral et un carré comme sur la figure.</p>'
        + `<p>On note ${tex('x')} la longueur du côté du triangle, en cm.</p>`
        + '<p><b>Quelle équation traduit que le triangle et le carré ont le même périmètre ?</b></p>';
    const choix = options.map((o) => tex(o.latex));
    const payload = makeQcm(choix, choix[0], texte,
        { ...opts(L_PERIMETRE), elements_additionnels: svgJuxtaposition(L, triangleAGauche) });
    payload._meta.params = { L, triangleAGauche, solution: fmtR(s), distracteurs: options.slice(1).map((o) => o.code) };
    return payload;
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'chaque distracteur est une erreur NOMMÉE d\'élève (codes dans _meta.params.distracteurs), jamais une option inventée à l\'assemblage',
        'chaque option est relue en polynôme exact (rationnels) : la clé s\'annule en la solution calculée depuis l\'histoire (throw sinon), aucun distracteur ne s\'y annule, nouveau tirage sinon',
        'quatre choix, tous écrits par tex() : une seule option en KaTeX se trahirait par la police',
    ],
    equation_qcm_unique: {
        billes: {
            support: 'T = 20k + d billes (k de 2 à 9, d de 6 à 30), x = nombre de billes noires = 10k ; clé x + (x + d) = T',
            familles: {
                une_seule_couleur: 'x + d = T : une seule couleur comptée',
                signe: 'x + (x − d) = T (l\'équation 2x − d = T écrite sous la forme de la clé) : l\'écart retranché au lieu d\'être ajouté',
                une_couleur_et_signe: 'x − d = T : les deux erreurs',
            },
            exclusion: 'd = 10k rendait une_seule_couleur et signe de même solution : nouveau tirage (quatre solutions distinctes exigées)',
        },
        machines: {
            support: 'Medhi ax − b, Sarah dd(x + c), a et dd de 2 à 6 DIFFÉRENTS (égaux : aucune solution), b et c de 2 à 9',
            grille: 'deux erreurs croisées, la clé et les trois autres cases : signe du b × parenthèses de Sarah',
            familles: {
                signe: 'ax + b = dd(x + c) : « soustrait » écrit +',
                sans_parentheses: 'ax − b = dd·x + c : le produit ne porte que sur x',
                les_deux: 'ax + b = dd·x + c',
            },
            exclusion: '(dd − 1)c = 2b rend signe et sans_parentheses de même solution : nouveau tirage',
        },
        prix: {
            support: 'p cahiers de 2 à 6 €, classeur q de 2 à 9 € DIFFÉRENT de p (p = q : prix_echanges équivalent à la clé), n de 4 à 9 cahiers, T = pn + q',
            grille: 'signe du prix fixe × prix échangés',
            familles: {
                signe: 'px − q = T',
                prix_echanges: 'qx + p = T : le prix fixe multiplié par le nombre de cahiers',
                les_deux: 'qx − p = T',
            },
        },
        abonnement: {
            support: 'carte C de 10 à 30 €, entrée e de 4 à 9 €, n de 3 à 12 entrées, T = en + C (l\'exemple du cours : 8x + 20 = 60)',
            grille: 'la même que prix',
            familles: {
                signe: 'ex − C = T',
                prix_echanges: 'Cx + e = T : la carte payée à chaque entrée',
                les_deux: 'Cx − e = T',
            },
        },
        supprimees: {
            age: 'contexte « Dans n ans, Léa aura A ans » (x + n = A, geste de 5e) remplacé par l\'abonnement (décision 2026-09-23)',
            ordre_medhi: 'a(x − b) = dd(x + c) : remplacée par la grille (la clé était l\'option la plus proche des trois autres)',
            x_non_multiplie: 'x + p + q = T : remplacée par la grille pour la même raison',
        },
    },
    traduire_qcm_unique: {
        support: 'une phrase sur « un nombre » (b de 2 à 9, de 3 à 5 pour produit ; c de 11 à 40), la clé une équation du premier degré ; les trois mauvaises traductions gardent LES nombres de la phrase',
        familles: {
            double: 'double_carre x² = c ; ajout_pour_double x + 2 = c (nom de 1110) ; sens_inverse x/2 = c (nom de 1110)',
            triple_augmente: 'parenthesage 3(x + b) = c ; ajout_pour_double x + 3 + b = c ; triple_cube x³ + b = c',
            produit: 'produit_somme x + b = c ; produit_puissance x^b = c ; produit_quotient x/b = c',
            double_diminue: 'grille parenthesage 2(x − b) = c × ordre_soustraction b − 2x = c, les_deux 2(b − x) = c ; double_carre x² − b = c écartée (la clé devenait l\'option la plus proche des trois autres)',
        },
        relier_seulement: 'somme (somme_produit bx = c, difference_pour_somme x − b = c), diminue (ordre_soustraction b − x = c, somme_pour_difference x + b = c, DEPP 4e item 3), moitie_augmentee (parenthesage (x + b)/2 = c, sens_inverse 2x + b = c) : deux erreurs nommées seulement, jamais au QCM',
    },
    traduire_relier: 'trois phrases de familles différentes, mêmes b et c, trois solutions distinctes ; deux leurres nommés tirés dans les erreurs de ces trois phrases, de deux familles différentes quand c\'est possible ; un leurre n\'est ni une cible ni vrai pour une des trois solutions',
    perimetre_qcm_unique: {
        support: 'longueur totale L de 10 à 20 cm, x = côté du triangle ; clé 3x = 4(L − x), solution 4L/7',
        familles: {
            parentheses_ignorees: '3x = 4 × L − x (solution L)',
            roles_echanges: '4x = 3(L − x) : le 3 et le 4 échangés (solution 3L/7)',
            donnee_oubliee: '3x = 4 × L : la longueur totale prise pour le côté du carré, le x retranché oublié (solution 4L/3)',
        },
        ecartee: {
            somme_pour_difference: '3x = 4(L + x) (DEPP 4e item 3) : écartée ici, la clé redevenait l\'option la plus proche des trois autres (mode auto, À ARBITRER)',
        },
    },
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    equation_qcm_unique: genEquationQcm,
    traduire_relier: genTraduireRelier,
    traduire_qcm_unique: genTraduireQcm,
    perimetre_qcm_unique: genPerimetreQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', relier: 'Relier',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'equation', label: L_EQUATION, formatsList: buildFormats('equation') },
    { id: 'traduire', label: L_TRADUIRE, formatsList: buildFormats('traduire') },
    { id: 'perimetre', label: L_PERIMETRE, formatsList: buildFormats('perimetre') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
